import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";

import {
  Search,
  User,
  RefreshCw,
  X,
  Package,
  ChevronRight,
  CheckCircle2,
  ShoppingCart,
  Printer,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { printPO, whatsappPO } from "@/utils/printPO";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PurchaseOrdersPanel = ({ onClose }) => {
  const { token, isFullAdmin, isManager } = useAuth();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [approvedPO, setApprovedPO] = useState(null);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, [search]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "Pending Approval" },
      });
      let pos = res.data.data || [];
      if (search) {
        pos = pos.filter(po => 
          (po.supplier_name && po.supplier_name.toLowerCase().includes(search.toLowerCase())) ||
          (po.po_number && po.po_number.toLowerCase().includes(search.toLowerCase()))
        );
      }
      setOrders(pos);
    } catch (err) {
      console.error("Failed to fetch pending purchase orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePO = async (po, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API_URL}/purchase-orders/${po.id}`, { status: "Pending" }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Purchase Order approved.");
      setApprovedPO(po);
      fetchOrders();
    } catch (err) {
      console.error("Failed to approve PO:", err);
      toast.error("Failed to approve Purchase Order");
    }
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 25 }}
      className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="flex items-center gap-2">
          <ShoppingCart size={20} />
          <h2 className="font-bold text-lg">Purchase Approvals</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b bg-stone-50">
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search supplier or PO #"
            className="pl-10 pr-10"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-stone-500">
            {orders.length} pending approval{orders.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={fetchOrders}
            className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Orders List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <AnimatePresence>
          {orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ShoppingCart size={48} className="text-stone-300 mb-3" />
              <p className="text-stone-400 text-sm">No POs awaiting approval</p>
            </div>
          ) : (
            orders.map((po) => (
              <motion.div
                key={po.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-blue-50 border-2 border-blue-200 hover:border-blue-400 rounded-xl p-4 cursor-default transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="font-bold text-sm text-blue-900 flex items-center gap-2">
                      {po.po_number || "Draft PO"}
                      <ChevronRight
                        size={14}
                        className="text-stone-400 group-hover:text-blue-500 transition-colors"
                      />
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {new Date(po.created_at).toLocaleString("en-IN", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <span className="text-lg font-mono font-bold text-blue-700">
                    ₹{Number(po.total_amount || 0).toFixed(2)}
                  </span>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs text-blue-800">
                    <User size={12} className="text-blue-500" />
                    <span className="font-semibold">{po.supplier_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-stone-600">
                    <User size={12} className="text-stone-400" />
                    <span>Req by: {po.generated_by || "Unknown"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-stone-600">
                    <Package size={12} className="text-stone-400" />
                    <span>
                      {po.items?.reduce((s, i) => s + i.quantity, 0) || 0} items
                    </span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-blue-100">
                  <div className="flex flex-wrap gap-1">
                    {(po.items || []).slice(0, 3).map((item, i) => (
                      <span
                        key={i}
                        className="text-[10px] bg-white text-blue-700 px-2 py-0.5 rounded-full shadow-sm"
                      >
                        {item.name} ×{item.quantity}
                      </span>
                    ))}
                    {(po.items || []).length > 3 && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold shadow-sm">
                        +{(po.items || []).length - 3} more
                      </span>
                    )}
                  </div>
                </div>
                
                {(isFullAdmin || isManager) && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={(e) => handleApprovePO(po, e)}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white hover:bg-blue-700"
                    >
                      <CheckCircle2 size={14} /> Approve PO
                    </button>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Approved Dialog */}
      <Dialog open={!!approvedPO} onOpenChange={(open) => !open && setApprovedPO(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 size={20} />
              PO Approved Successfully!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <p className="text-sm text-stone-500 mb-6">
              Purchase Order <strong>{approvedPO?.po_number || 'Draft'}</strong> has been approved. What would you like to do next?
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => printPO(approvedPO)} className="w-full flex items-center justify-center gap-2 bg-stone-800 hover:bg-stone-900 text-white">
                <Printer size={16} />
                Download / Print PDF
              </Button>
              <Button onClick={() => whatsappPO(approvedPO)} className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white">
                <MessageCircle size={16} />
                Send on WhatsApp
              </Button>
              <Button variant="outline" onClick={() => setApprovedPO(null)} className="w-full mt-2">
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default PurchaseOrdersPanel;
